import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, BookOpen, Check, Loader2, MapPin, Plus } from 'lucide-react';
import { toast } from 'sonner';
import Modal from './Modal';
import {
  useSetTutorWishlistMutation,
  useTutorWishlistCaseOptions,
} from '@/hooks/api/useWishlistTutors';
import { useModal } from '@/contexts';
import type { TutorWishlistCaseOption, TutorWishlistCaseStatus } from '@/types/api';
import { logger } from '@/utils/logger';
import { pushBreadcrumb } from '@/debug/debugStore';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';
import { useNavigate } from '@/i18n/navigation';

interface WishlistCaseSelectionModalProps {
  isOpen: boolean;
  tutorId?: string;
  tutorName?: string;
  onClose: () => void;
}

const STATUS_LABELS: Record<TutorWishlistCaseStatus, string> = {
  pending_review: 'Under review',
  new: 'New',
  pending: 'Pending',
  action_needed: 'Action needed',
  awaiting_payment: 'Awaiting payment',
  rejected: 'Rejected',
  closed: 'Closed',
};

const WishlistCaseSelectionModal = ({
  isOpen,
  tutorId,
  tutorName,
  onClose,
}: WishlistCaseSelectionModalProps) => {
  const { locale } = useOptionalI18n();
  const navigate = useNavigate();
  const { openParentRegistration } = useModal();
  const optionsQuery = useTutorWishlistCaseOptions(tutorId, isOpen);
  const mutation = useSetTutorWishlistMutation();
  const [selectedCaseIds, setSelectedCaseIds] = useState<Set<string>>(new Set());
  const initializedTutorRef = useRef<string | null>(null);
  const isZh = locale === 'zh-CN';

  useEffect(() => {
    if (!isOpen) {
      initializedTutorRef.current = null;
      return;
    }
    if (!optionsQuery.data || initializedTutorRef.current === tutorId) return;
    setSelectedCaseIds(
      new Set(optionsQuery.data.cases.filter((item) => item.isLinked).map((item) => item.id))
    );
    initializedTutorRef.current = tutorId ?? null;
    logger.log('WishlistCaseSelectionModal', 'Initialized case selection', {
      tutorId,
      linkedCaseIds: optionsQuery.data.cases.filter((item) => item.isLinked).map((item) => item.id),
      eligibleCaseIds: optionsQuery.data.cases
        .filter((item) => item.isEligibleForNewLink)
        .map((item) => item.id),
    }, true);
  }, [isOpen, optionsQuery.data, tutorId]);

  const selectableCases = useMemo(
    () =>
      optionsQuery.data?.cases.filter(
        (item) => item.isLinked || (item.isEligibleForNewLink && optionsQuery.data?.tutorAvailable)
      ) ?? [],
    [optionsQuery.data]
  );
  const initiallyLinkedCaseIds = useMemo(
    () => new Set(
      optionsQuery.data?.cases.filter((item) => item.isLinked).map((item) => item.id) ?? []
    ),
    [optionsQuery.data]
  );
  const hasSelectionChanges =
    initializedTutorRef.current === tutorId &&
    (selectedCaseIds.size !== initiallyLinkedCaseIds.size ||
      Array.from(selectedCaseIds).some((caseId) => !initiallyLinkedCaseIds.has(caseId)));
  const hasLinkedCases = optionsQuery.data?.cases.some((item) => item.isLinked) ?? false;
  const hasEligibleCases =
    optionsQuery.data?.cases.some((item) => item.isEligibleForNewLink) ?? false;
  const showNoCasePrompt =
    optionsQuery.isSuccess && !hasLinkedCases && !hasEligibleCases;

  const toggleCase = (item: TutorWishlistCaseOption) => {
    const canToggle =
      item.isLinked || (item.isEligibleForNewLink && optionsQuery.data?.tutorAvailable);
    if (!canToggle || mutation.isPending) return;

    setSelectedCaseIds((current) => {
      const next = new Set(current);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      logger.log('WishlistCaseSelectionModal', 'Toggled case selection', {
        tutorId,
        toggledCaseId: item.id,
        selectedCaseIds: Array.from(next),
      }, true);
      return next;
    });
  };

  const handleSave = () => {
    if (!tutorId || !hasSelectionChanges) return;
    const caseIds = Array.from(selectedCaseIds);
    const debugRequestId = globalThis.crypto?.randomUUID?.() ?? `wishlist-${Date.now()}`;
    logger.log('WishlistCaseSelectionModal', 'Submitting case selection', {
      debugRequestId,
      tutorId,
      caseIds,
      selectedCount: caseIds.length,
      serverLinkedCaseIds: optionsQuery.data?.cases
        .filter((item) => item.isLinked)
        .map((item) => item.id) ?? [],
    }, true);
    pushBreadcrumb({
      category: 'api',
      message: 'Submit tutor wishlist cases',
      metadata: { debugRequestId, tutorId, caseIds, selectedCount: caseIds.length },
    });
    mutation.mutate(
      { tutorId, caseIds, debugRequestId },
      {
        onSuccess: (response) => {
          logger.log('WishlistCaseSelectionModal', 'Case selection response', {
            debugRequestId,
            tutorId,
            requestedCaseIds: caseIds,
            linkedCaseIds: response.linkedCaseIds,
            isWishlisted: response.isWishlisted,
          }, true);
          pushBreadcrumb({
            category: 'api',
            message: 'Tutor wishlist cases updated',
            metadata: {
              debugRequestId,
              tutorId,
              requestedCaseIds: caseIds,
              linkedCaseIds: response.linkedCaseIds,
              isWishlisted: response.isWishlisted,
            },
          });
          toast.success(
            response.isWishlisted
              ? isZh
                ? `${tutorName ?? '该导师'} 已保存到 ${response.linkedCaseIds.length} 个个案`
                : `${tutorName ?? 'Tutor'} saved to ${response.linkedCaseIds.length} case${response.linkedCaseIds.length === 1 ? '' : 's'}`
              : isZh
                ? `${tutorName ?? '该导师'} 已从收藏导师中移除`
                : `${tutorName ?? 'Tutor'} removed from liked tutors`
          );
          onClose();
        },
        onError: (error) => {
          logger.error('WishlistCaseSelectionModal', 'Case selection request failed', {
            debugRequestId,
            tutorId,
            requestedCaseIds: caseIds,
            error,
          }, true);
          pushBreadcrumb({
            category: 'api',
            message: 'Tutor wishlist cases update failed',
            metadata: { debugRequestId, tutorId, requestedCaseIds: caseIds },
          });
          toast.error(isZh ? '更新收藏导师关联个案失败，请再试一次。' : 'Could not update liked tutor cases. Please try again.');
        },
      }
    );
  };

  const footer = showNoCasePrompt ? undefined : (
    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-gray-600" aria-live="polite">
        {isZh
          ? `已选择 ${selectedCaseIds.size} 个个案`
          : `${selectedCaseIds.size} case${selectedCaseIds.size === 1 ? '' : 's'} selected`}
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="min-h-11 cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isZh ? '取消' : 'Cancel'}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={
            mutation.isPending ||
            optionsQuery.isLoading ||
            optionsQuery.isError ||
            !hasSelectionChanges
          }
          className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {mutation.isPending ? (isZh ? '保存中…' : 'Saving…') : hasSelectionChanges ? (isZh ? '保存个案' : 'Save cases') : (isZh ? '没有更改' : 'No changes')}
        </button>
      </div>
    </div>
  );

  return (
      <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={hasLinkedCases ? (isZh ? '管理收藏导师' : 'Manage liked tutor') : (isZh ? '为此导师选择个案' : 'Choose cases for this tutor')}
      maxWidth="max-w-2xl"
      footer={footer}
      closeOnOverlayClick={!mutation.isPending}
    >
      <div aria-live="polite">
        <p className="mb-5 text-sm leading-6 text-gray-600">
          {isZh
            ? `把 ${tutorName ?? '这位导师'} 关联到你希望团队优先考虑的补习个案。`
            : `Link ${tutorName ?? 'this tutor'} to the tuition cases where you would like our team to consider them.`}
        </p>

        {optionsQuery.isLoading && (
          <div className="flex min-h-48 items-center justify-center gap-3 text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
            {isZh ? '正在加载你的个案…' : 'Loading your cases…'}
          </div>
        )}

        {optionsQuery.isError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-800">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-none" aria-hidden="true" />
              <div>
                <p className="font-semibold">{isZh ? '无法加载你的个案' : 'Could not load your cases'}</p>
                <p className="mt-1 text-sm">{isZh ? '请在不关闭此窗口的情况下重试。' : 'Please retry without closing this window.'}</p>
                <button
                  type="button"
                  onClick={() => optionsQuery.refetch()}
                  className="mt-4 min-h-11 cursor-pointer rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {isZh ? '重试' : 'Try again'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showNoCasePrompt && (
          <div className="rounded-2xl border border-primary-100 bg-primary-50 p-6 text-center">
            <BookOpen className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
            <h4 className="mt-3 text-lg font-semibold text-gray-900">{isZh ? '请先发布个案' : 'Post a case first'}</h4>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-600">
              {isZh ? '导师只能新关联到当前正在配对中的个案。' : 'Tutors can only be newly linked to cases that are currently matching.'}
            </p>
            <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  openParentRegistration();
                }}
                className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {isZh ? '发布新个案' : 'Post New Case'}
              </button>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/parent/my-cases');
                }}
                className="min-h-11 cursor-pointer rounded-lg border border-primary-200 bg-white px-5 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                {isZh ? '前往我的个案' : 'Go to My Cases'}
              </button>
            </div>
          </div>
        )}

        {optionsQuery.data && !showNoCasePrompt && (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCaseIds(new Set(selectableCases.map((item) => item.id)))}
                  disabled={mutation.isPending || selectableCases.length === 0}
                  className="min-h-11 cursor-pointer rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isZh ? '全选' : 'Select all'}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedCaseIds(new Set())}
                  disabled={mutation.isPending || selectedCaseIds.size === 0}
                  className="min-h-11 cursor-pointer rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isZh ? '清除全部' : 'Clear all'}
                </button>
              </div>
              {!optionsQuery.data.tutorAvailable && (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                  {isZh ? '导师暂不可用，仅可保留现有关联' : 'Tutor unavailable — existing links only'}
                </span>
              )}
            </div>

            <div className="space-y-3">
              {optionsQuery.data.cases.map((item) => {
                const isSelected = selectedCaseIds.has(item.id);
                const canToggle =
                  item.isLinked || (item.isEligibleForNewLink && optionsQuery.data.tutorAvailable);
                return (
                  <label
                    key={item.id}
                    className={`flex min-h-24 rounded-xl border p-4 transition-colors ${
                      canToggle ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
                    } ${
                      isSelected
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={!canToggle || mutation.isPending}
                      onChange={() => toggleCase(item)}
                      className="sr-only"
                    />
                    <span
                      className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-md border ${
                        isSelected
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-300 bg-white text-transparent'
                      }`}
                      aria-hidden="true"
                    >
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="ml-3 min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900">{localizeText(locale, item.subject)}</span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                          {localizeText(locale, item.level)}
                        </span>
                        <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
                          {localizeText(locale, STATUS_LABELS[item.status])}
                        </span>
                      </span>
                      <span className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="h-4 w-4 text-gray-400" aria-hidden="true" />
                          {item.tuitionType === 'online'
                            ? (isZh ? '线上' : 'Online')
                            : [item.district, item.state ? localizeText(locale, item.state) : item.state].filter(Boolean).join(', ') || (isZh ? '未设置地点' : 'Location not set')}
                        </span>
                        <span>{item.hourlyRate ? `RM ${item.hourlyRate}/${isZh ? '小时' : 'hr'}` : (isZh ? '时薪待商议' : 'Rate to be discussed')}</span>
                      </span>
                      {!item.isEligibleForNewLink && item.isLinked && (
                        <span className="mt-2 block text-xs font-medium text-amber-700">
                          {isZh ? '此关联可以保留或移除，但当前状态下不能重新新增。' : 'This link can be kept or removed, but cannot be newly added in this status.'}
                        </span>
                      )}
                      {item.status === 'pending_review' && (
                        <span className="mt-2 block text-xs font-medium text-amber-700">
                          {isZh ? '等待管理员审核中，这个个案暂未在平台公开。' : 'Awaiting admin approval — this case is not on the Marketplace yet.'}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default WishlistCaseSelectionModal;
